import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import { loginSerializer } from './login/loginSerializer';
import { registrationSerializer } from './registration/registrationSerializer';
import { updateSerializer } from './update/updateSerializer';
import { User } from './user';
import { authenticatedRequest } from '@core/utils/authenticatedRequest';
import { transformRestaurants } from '@features/home/serializers';
import dishSerializer from '@features/dishes/serializer';
import { notificationService } from '@features/services/notificationService';
import Commons from '@features/shared/libraries/commons';

async function syncDeviceToken(userId) {
  console.log('🚀 ~ syncDeviceToken ~ userId:', userId);
  if (!userId) return;
  try {
    let token = await notificationService.getDeviceToken();

    if (token) {
      console.log('[Auth Controller] Got device token, syncing:', token);

      // Upload to messaging microservice
      await notificationService.uploadDeviceToken(userId, token);

      // Update user metadata so token is saved in user object too
      const userStore = getStore('user');
      const currentUser = userStore?.state?.current;

      if (currentUser?.metadata?.deviceToken !== token) {
        bus.emit(constants.EVENTS.USER.UPDATE, {
          metadata: {
            ...currentUser?.metadata,
            deviceToken: token
          }
        });
      }
    } else {
      console.log(
        '[Auth] No device token found (browser or permission denied)'
      );
    }
  } catch (error) {
    console.error('[Auth Controller] Could not get device token', error);
  }
}

async function persistUserToNativeStorage() {
  if (!Commons.isCapacitor()) return;
  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;
  if (currentUser?.id) {
    const ok = await Commons.syncUserDataToPreferences(currentUser);
    if (ok) console.log('💾 user_data persisté en natif');
  }
}

let refreshTimeout = null;

function refreshUserData() {
  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;

  if (!currentUser?.id) {
    return;
  }

  const requestId = `refreshUserData-${Date.now()}`;
  let url = `${constants.ENDPOINTS.USER.REFRESH_DATA}`;
  url = url.replace('{userId}', currentUser.id);

  authenticatedRequest(bus, {
    id: requestId,
    url,
    method: 'GET',
    requiresAuth: true
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);

      const parsed = updateSerializer.deserialize(res.data || res.error);

      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.REFRESH_DATA_SUCCESS, parsed.user);
        syncDeviceToken(parsed.user.id);
      } else if (
        res.data?.statusCode === 404 ||
        res.error?.statusCode === 404
      ) {
        bus.emit(constants.EVENTS.USER.LOGOUT);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Compte supprimé',
          body: "Votre compte n'existe plus. Vous avez été déconnecté."
        });

        setTimeout(() => {
          window.location.reload();
        }, 5000);
      } else {
        bus.emit(constants.EVENTS.USER.REFRESH_DATA_ERROR, parsed);
      }
    }
  };
  bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
}

function startAutoRefresh() {
  if (refreshTimeout) {
    clearInterval(refreshTimeout);
  }

  refreshUserData();

  refreshTimeout = setInterval(
    () => {
      refreshUserData();
    },
    5 * 60 * 1000
  );
}

function stopAutoRefresh() {
  if (refreshTimeout) {
    clearInterval(refreshTimeout);
    refreshTimeout = null;
  }
}

// controller.js

// Fonction utilitaire pour émettre des erreurs de connexion vers la vue
function notifyLoginError(title, message) {
  setTimeout(() => {
    bus.emit(constants.EVENTS.USER.LOGIN_ERROR, { message });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: title || 'Erreur de connexion',
      body: message
    });
  }, 1000);
}

// Fonction centrale pour exécuter la requête POST de Login
function performLogin(formData) {
  const reqData = loginSerializer.serialize(formData);
  const requestId = `login-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${constants.ENDPOINTS.USER.LOGIN}`,
    method: 'POST',
    data: reqData
  });

  const loginHandler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, loginHandler);
      const parsed = loginSerializer.deserialize(res.data || res.error);
      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.LOGIN_SUCCESS, {
          user: parsed.user,
          userId: parsed.user.id
        });
        syncDeviceToken(parsed.user.id);
        if (formData.updateAfterLogin) {
          bus.emit(constants.EVENTS.USER.UPDATE, formData);
        }
      } else {
        notifyLoginError(parsed.errorTitle, parsed.message);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, loginHandler);
}

// Écouteur d'évènement LOGIN mis à jour
bus.on(constants.EVENTS.USER.LOGIN, (formData) => {
  const rawInput = (formData?.email || formData?.login || '').trim();

  // 1. S'assurer qu'un email ou un numéro a été renseigné
  if (!rawInput) {
    notifyLoginError(
      'Champs requis',
      'Veuillez saisir votre email ou votre numéro de téléphone.'
    );
    return;
  }

  // Expression régulière standard pour vérifier si c'est un e-mail
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isEmail = emailRegex.test(rawInput);

  if (isEmail) {
    // Si c'est déjà un e-mail, exécuter le flux de connexion normal
    formData.email = rawInput;
    performLogin(formData);
  } else {
    // 2. Traitement du numéro de téléphone
    // On retire tous les caractères non numériques
    let digits = rawInput.replace(/\D/g, '');

    // Si l'utilisateur a déjà inclus l'indicatif 229 au début, on le retire pour normaliser
    if (digits.startsWith('229')) {
      digits = digits.slice(3);
    }

    let finalPhone = '';

    if (digits.length === 10) {
      if (!digits.startsWith('01')) {
        notifyLoginError(
          'Numéro invalide',
          'Un numéro à 10 chiffres doit obligatoirement commencer par 01.'
        );
        return;
      }
      finalPhone = `229${digits}`;
    } else if (digits.length === 8) {
      finalPhone = `22901${digits}`;
    } else {
      notifyLoginError(
        'Numéro invalide',
        'Le numéro de téléphone doit contenir au moins 8 chiffres (ou 10 chiffres commençant par 01).'
      );
      return;
    }

    // 3. Récupération de l'utilisateur à partir du numéro de téléphone
    const fetchUserReqId = `fetch-user-by-phone-${Date.now()}`;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: fetchUserReqId,
      url: constants.ENDPOINTS.USER.GET_ALL(finalPhone),
      method: 'GET'
    });

    const phoneHandler = (res) => {
      if (res.id === fetchUserReqId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, phoneHandler);

        const usersData = res.data?.data || res.data || [];
        const user =
          Array.isArray(usersData) && usersData.length > 0
            ? usersData[0]
            : null;

        if (res.success && user && user.email) {
          // E-mail trouvé, lancer le login
          formData.email = user.email;
          performLogin(formData);
        } else {
          notifyLoginError(
            'Utilisateur non trouvé',
            'Aucun compte associé à ce numéro de téléphone.'
          );
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, phoneHandler);
  }
});

// Fonction utilitaire pour émettre des erreurs d'inscription vers la vue
function notifyRegisterError(title, message) {
  setTimeout(() => {
    bus.emit(constants.EVENTS.USER.REGISTER_ERROR, { message });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: title || "Erreur d'inscription",
      body: message
    });
  }, 300);
}

// Fonction centrale pour exécuter l'inscription POST
function performRegistration(formData) {
  const reqData = registrationSerializer.serialize(formData);
  const requestId = `register-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url: `${constants.ENDPOINTS.USER.REGISTER}`,
    method: 'POST',
    data: reqData
  });

  const registerHandler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, registerHandler);
      const parsed = registrationSerializer.deserialize(res.data || res.error);
      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.REGISTER_SUCCESS, {
          user: parsed.user,
          userId: parsed.user.id
        });
        syncDeviceToken(parsed.user.id);
      } else {
        if (parsed.alreadyExists && formData.tryLoginOnExist) {
          bus.emit(constants.EVENTS.USER.LOGIN, formData);
          return;
        }
        notifyRegisterError(parsed.errorTitle, parsed.message);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, registerHandler);
}

bus.on(constants.EVENTS.USER.REGISTER, (formData) => {
  // 1. Validation de la confirmation du mot de passe
  if (
    formData.passwordConfirm &&
    formData.password !== formData.passwordConfirm
  ) {
    notifyRegisterError(
      'Mots de passe non identiques',
      'Les mots de passe saisis ne correspondent pas.'
    );
    return;
  }

  // 2. Validation et normalisation du numéro de téléphone
  const rawPhone = (formData?.phoneNumber || '').trim();
  let prefix = (formData?.phonePrefix || '229').replace(/\D/g, '');
  let digits = rawPhone.replace(/\D/g, '');

  if (!digits) {
    notifyRegisterError(
      'Numéro requis',
      'Veuillez saisir votre numéro de téléphone.'
    );
    return;
  }

  let finalPhone = '';

  if (prefix === '229') {
    if (digits.startsWith('229')) {
      digits = digits.slice(3);
    }

    if (digits.length === 10) {
      if (!digits.startsWith('01')) {
        notifyRegisterError(
          'Numéro invalide',
          'Un numéro à 10 chiffres doit obligatoirement commencer par 01.'
        );
        return;
      }
      finalPhone = `229${digits}`;
      formData.phoneNumber = digits;
    } else if (digits.length === 8) {
      finalPhone = `22901${digits}`;
      formData.phoneNumber = `01${digits}`;
    } else {
      notifyRegisterError(
        'Numéro invalide',
        'Le numéro de téléphone doit contenir au moins 8 chiffres (ou 10 chiffres commençant par 01).'
      );
      return;
    }
  } else {
    if (digits.length < 8 || digits.length > 15) {
      notifyRegisterError(
        'Numéro invalide',
        'Veuillez saisir un numéro de téléphone valide.'
      );
      return;
    }
    finalPhone = `${prefix}${digits}`;
    formData.phoneNumber = digits;
  }

  formData.phonePrefix = prefix;

  // 3. Vérification de l'existence préalable du numéro de téléphone
  const checkPhoneReqId = `check-register-phone-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: checkPhoneReqId,
    url: constants.ENDPOINTS.USER.GET_ALL(finalPhone),
    method: 'GET'
  });

  const checkPhoneHandler = (phoneRes) => {
    if (phoneRes.id === checkPhoneReqId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, checkPhoneHandler);

      const usersData = phoneRes.data?.data || phoneRes.data || [];
      const userExists = Array.isArray(usersData) && usersData.length > 0;

      if (phoneRes.success && userExists) {
        notifyRegisterError(
          'Numéro déjà utilisé',
          'Un compte existe déjà avec ce numéro de téléphone. Veuillez vous connecter.'
        );
        return;
      }

      // 4. Si un email est renseigné, vérifier s'il existe déjà
      const rawEmail = (formData.email || '').trim();
      if (rawEmail) {
        const checkEmailReqId = `check-register-email-${Date.now()}`;

        bus.emit(constants.EVENTS.NETWORK.REQUEST, {
          id: checkEmailReqId,
          url: constants.ENDPOINTS.USER.GET_BY_EMAIL(rawEmail),
          method: 'GET'
        });

        const checkEmailHandler = (emailRes) => {
          if (emailRes.id === checkEmailReqId) {
            bus.off(constants.EVENTS.NETWORK.RESPONSE, checkEmailHandler);

            const emailUsersData = emailRes.data?.data || emailRes.data || [];
            const emailUserExists =
              Array.isArray(emailUsersData) && emailUsersData.length > 0;

            if (emailRes.success && emailUserExists) {
              notifyRegisterError(
                'Email déjà utilisé',
                'Un compte existe déjà avec cette adresse email.'
              );
              return;
            }

            // Tout est valide -> Lancer l'inscription
            performRegistration(formData);
          }
        };

        bus.on(constants.EVENTS.NETWORK.RESPONSE, checkEmailHandler);
      } else {
        // Pas d'email fourni -> Lancer l'inscription directement
        performRegistration(formData);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, checkPhoneHandler);
});

bus.on(constants.EVENTS.USER.UPDATE, (updateData) => {
  console.log('🚀 ~ updateData:', updateData);
  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;

  const email = updateData.email || currentUser?.email;

  if (!email) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Aucun email fourni pour la mise à jour'
    });
    bus.emit(constants.EVENTS.USER.UPDATE_ERROR, {
      message: 'Aucun email fourni'
    });
    return;
  }

  const isGuestUpdate = !currentUser?.email && !!updateData.email;

  bus.emit(constants.EVENTS.LOADING.START);
  const reqData = updateSerializer.serialize({
    ...updateData,
    email
  });
  const requestId = `update-${Date.now()}`;

  authenticatedRequest(bus, {
    id: requestId,
    url: `${constants.ENDPOINTS.USER.UPDATE}`,
    method: 'POST',
    data: reqData,
    requiresAuth: true,
    errorEvent: constants.EVENTS.USER.UPDATE_ERROR
  });

  bus.on(constants.EVENTS.NETWORK.RESPONSE, (res) => {
    if (res.id === requestId) {
      bus.emit(constants.EVENTS.LOADING.FINISH);
      const parsed = updateSerializer.deserialize(res.data || res.error);
      if (parsed.success) {
        bus.emit(constants.EVENTS.USER.UPDATE_SUCCESS, parsed.user);
      } else {
        bus.emit(constants.EVENTS.USER.UPDATE_ERROR, res);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: parsed.errorTitle,
          body: parsed.message
        });
      }
    }
  });
});

bus.on(constants.EVENTS.USER.LOGIN_SUCCESS, () => {
  startAutoRefresh();
  persistUserToNativeStorage();
});

bus.on(constants.EVENTS.USER.REGISTER_SUCCESS, () => {
  persistUserToNativeStorage();
});

bus.on(constants.EVENTS.USER.UPDATE_SUCCESS, () => {
  persistUserToNativeStorage();
});

bus.on(constants.EVENTS.USER.REFRESH_DATA_SUCCESS, () => {
  persistUserToNativeStorage();
});

bus.on(constants.EVENTS.USER.LOGOUT, async () => {
  stopAutoRefresh();

  // Nettoyer le Preferences natif AVANT de vider localStorage
  try {
    await Commons.clearUserDataFromPreferences();
  } catch (e) {
    console.warn('Failed to clear Preferences', e);
  }

  localStorage.clear();
  window.location.reload();
});

// Handle birthday save - now uses USER.UPDATE event
bus.on(constants.EVENTS.USER.SAVE_BIRTHDAY, (formData) => {
  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;

  if (!currentUser?.id) {
    bus.emit(constants.EVENTS.USER.SAVE_BIRTHDAY_ERROR, {
      message: 'Utilisateur non connecté'
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Vous devez être connecté pour effectuer cette action'
    });
    return;
  }

  if (!formData.birthday && !formData.date) {
    bus.emit(constants.EVENTS.USER.SAVE_BIRTHDAY_ERROR, {
      message: "Date d'anniversaire requise"
    });
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: "Veuillez sélectionner votre date d'anniversaire"
    });
    return;
  }

  const birthday = formData.birthday || formData.date;

  // Format birthday to ISO string and emit USER.UPDATE
  const formattedBirthday = new Date(birthday).toISOString();

  bus.emit(constants.EVENTS.USER.UPDATE, {
    metadata: {
      birthday: formattedBirthday
    }
  });
});

bus.on(constants.EVENTS.USER.REFRESH_DATA, () => {
  refreshUserData();
});

bus.on(constants.EVENTS.APP.READY, async () => {
  const userStore = getStore('user');
  const currentUser = userStore?.state?.current;

  // 🔁 Restauration depuis Preferences si l'app se relance sans user en mémoire
  if (!currentUser?.id && Commons.isCapacitor()) {
    console.log('🔎 Tentative de restauration depuis Preferences...');
    const persistedUser = await Commons.requestUserDataFromPreferences();
    if (persistedUser?.id) {
      console.log('🔁 User restauré:', persistedUser.id);
      // Injecter dans le store via l'event existant
      bus.emit(constants.EVENTS.USER.REFRESH_DATA_SUCCESS, persistedUser);
      bus.emit(constants.EVENTS.USER.LOGIN_SUCCESS, {
        user: persistedUser,
        userId: persistedUser.id
      });
      startAutoRefresh();
      syncDeviceToken(persistedUser.id);
    }
  }

  refreshUserData();

  const user = getStore('user')?.state?.current;
  if (user?.id) {
    syncDeviceToken(user.id);
  }
});

// Favorites Toggle Logic
bus.on(
  constants.EVENTS.USER.FAVORITE_TOGGLE_REST,
  ({ restaurantId, isFavorite }) => {
    const userStore = getStore('user');
    const userId = userStore?.state?.current?.id;

    if (!userId) return;

    const requestId = `fav-rest-${restaurantId}-${Date.now()}`;
    const url = constants.ENDPOINTS.USER.FAVORITES.TOGGLE;

    authenticatedRequest(bus, {
      id: requestId,
      url,
      method: 'POST',
      requiresAuth: true,
      data: {
        userId,
        targetId: restaurantId,
        targetType: 'restaurant'
      }
    });

    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
        if (res.data?.success || res.data?.statusCode === 200) {
          bus.emit(constants.EVENTS.USER.FAVORITE_SUCCESS, {
            type: 'restaurant',
            id: restaurantId,
            isFavorite: !isFavorite
          });
          if (!isFavorite) {
            bus.emit(constants.EVENTS.RESTAURANT.UPDATE_STATS_REQUESTED, {
              restaurantId,
              action: 'incrementFavorites',
              value: 1
            });
          } else {
            // decrement is not supported yet
            // bus.emit(constants.EVENTS.RESTAURANT.UPDATE_STATS_REQUESTED, {
            //   restaurantId,
            //   action: 'decrementFavorites',
            //   value: 1
            // });
          }
          // Refresh full favorites list to keep favorites store + user store IDs in sync
          bus.emit(constants.EVENTS.USER.FAVORITES_LOAD);
        } else {
          bus.emit(constants.EVENTS.USER.FAVORITE_ERROR, {
            type: 'restaurant',
            id: restaurantId,
            error: res.error
          });
        }
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
  }
);

bus.on(constants.EVENTS.USER.FAVORITE_TOGGLE_DISH, ({ dishId, isFavorite }) => {
  const userStore = getStore('user');
  const userId = userStore?.state?.current?.id;

  if (!userId) return;

  const requestId = `fav-dish-${dishId}-${Date.now()}`;
  const url = constants.ENDPOINTS.USER.FAVORITES.TOGGLE;

  // Optimistic UI update — update store immediately
  bus.emit(constants.EVENTS.USER.FAVORITE_SUCCESS, {
    type: 'dish',
    id: dishId,
    isFavorite: !isFavorite
  });

  authenticatedRequest(bus, {
    id: requestId,
    url,
    method: 'POST',
    requiresAuth: true,
    data: {
      userId,
      targetId: dishId,
      targetType: 'dish'
    }
  });

  const handler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);
      if (res.data?.success || res.data?.statusCode === 200) {
        // Optimistic update already applied — refresh full list to confirm server state
        bus.emit(constants.EVENTS.USER.FAVORITES_LOAD);
      } else {
        // Revert on failure
        bus.emit(constants.EVENTS.USER.FAVORITE_SUCCESS, {
          type: 'dish',
          id: dishId,
          isFavorite: isFavorite
        });
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          type: 'error',
          title: 'Erreur',
          body: 'Impossible de modifier les favoris. Veuillez réessayer.'
        });
      }
    }
  };
  bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
});
bus.on(constants.EVENTS.USER.FAVORITES_LOAD, () => {
  const userStore = getStore('user');
  const userId = userStore?.state?.current?.id;

  if (!userId) {
    bus.emit(constants.EVENTS.USER.FAVORITES_LOAD_SUCCESS, {
      restaurants: [],
      dishes: []
    });
    return;
  }

  const loadRequestId = `load-favs-${Date.now()}`;
  let favorites = { restaurants: [], dishes: [] };

  // Fetch the mapping of all favorites for the user
  const url = `${constants.ENDPOINTS.USER.FAVORITES.LIST}&userId=${userId}&limit=1000`;

  authenticatedRequest(bus, {
    id: loadRequestId,
    url,
    method: 'GET',
    requiresAuth: true
  });

  const loadHandler = (res) => {
    if (res.id === loadRequestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, loadHandler);

      if (res.success && (res.data?.success || res.data?.statusCode === 200)) {
        const rawFavorites = res.data?.data?.data || res.data?.data || [];

        // Extract IDs by type
        const restaurantIds = rawFavorites
          .filter((f) => f.targetType === 'restaurant')
          .map((f) => f.targetId);
        const dishIds = rawFavorites
          .filter((f) => f.targetType === 'dish')
          .map((f) => f.targetId);

        let pendingRequests = 0;

        const checkCompletion = () => {
          if (pendingRequests === 0) {
            bus.emit(constants.EVENTS.USER.FAVORITES_LOAD_SUCCESS, favorites);
          }
        };

        // If no favorites at all
        if (restaurantIds.length === 0 && dishIds.length === 0) {
          checkCompletion();
          return;
        }

        // Fetch Restaurant details if needed
        if (restaurantIds.length > 0) {
          pendingRequests++;
          const restReqId = `fav-rest-details-${Date.now()}`;
          // Fetch all restaurants and filter locally (assuming no detailsByIds endpoint for restaurants)
          authenticatedRequest(bus, {
            id: restReqId,
            url: '/restaurant/list?group=ultraxeno',
            method: 'GET',
            requiresAuth: false
          });

          const restDetailHandler = (restRes) => {
            if (restRes.id === restReqId) {
              bus.off(constants.EVENTS.NETWORK.RESPONSE, restDetailHandler);
              if (restRes.success) {
                const allRests =
                  restRes.data?.data?.data ||
                  restRes.data?.data ||
                  restRes.data?.restaurants ||
                  [];
                const transformedRests = transformRestaurants(allRests);
                favorites.restaurants = transformedRests.filter((r) =>
                  restaurantIds.includes(r.id)
                );
              }
              pendingRequests--;
              checkCompletion();
            }
          };
          bus.on(constants.EVENTS.NETWORK.RESPONSE, restDetailHandler);
        }

        // Fetch Dish details if needed
        if (dishIds.length > 0) {
          pendingRequests++;
          const dishReqId = `fav-dish-details-${Date.now()}`;
          authenticatedRequest(bus, {
            id: dishReqId,
            url: `/dish/detailsByIds?group=ultraxeno&ids=${dishIds.join(',')}`,
            method: 'GET',
            requiresAuth: false
          });

          const dishDetailHandler = (dishRes) => {
            if (dishRes.id === dishReqId) {
              bus.off(constants.EVENTS.NETWORK.RESPONSE, dishDetailHandler);
              if (dishRes.success) {
                const rawDishes =
                  dishRes.data?.data?.data || dishRes.data?.data || [];
                favorites.dishes = dishSerializer.deserializeList({
                  data: rawDishes
                });
              }
              pendingRequests--;
              checkCompletion();
            }
          };
          bus.on(constants.EVENTS.NETWORK.RESPONSE, dishDetailHandler);
        }
      } else {
        bus.emit(constants.EVENTS.USER.FAVORITES_LOAD_ERROR, res.error);
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, loadHandler);
});
